/**
 * ContentMainContainer - Orchestrates Breakpoints, Languages, and Themes
 *
 * Composes the three inner controls with deterministic ordering and separators.
 * Provides visibility toggles per inner component and an idempotent update API.
 */

import type { Theme } from "@/types/user-types.js";
import type { Language } from "@/types/user-types.js";
import type { BreakpointType } from "../Breakpoints/Breakpoints.js";
import {
  createBreakpoints,
  type BreakpointsAPI,
} from "../Breakpoints/index.js";
import { createThemes, type ThemesAPI } from "../Themes/index.js";
import { createLanguages, type LanguagesAPI } from "../Languages/index.js";
import { createSeparator } from "@/utils/separator";
import "./ContentMainContainer.scss";

export interface ContentMainVisibilityConfig {
  readonly showBreakpoints?: boolean;
  readonly showLanguages?: boolean;
  readonly showThemes?: boolean;
}

export interface ContentMainConfig extends ContentMainVisibilityConfig {
  readonly selectedBreakpoint?: BreakpointType;
  readonly selectedTheme?: Theme;
  readonly selectedLanguage?: Language;
  readonly availableLanguages?: readonly Language[];
  readonly onBreakpointChange?: (bp: BreakpointType) => void;
  readonly onThemeChange?: (theme: Theme) => void;
  readonly onLanguageChange?: (language: Language) => void;
}

export interface ContentMainAPI {
  readonly getElement: () => HTMLElement;
  readonly update: (partial: Partial<ContentMainConfig>) => void;
  readonly setVisibility: (visibility: ContentMainVisibilityConfig) => void;
  readonly destroy: () => void;
}

export const createContentMainContainer = (
  config: ContentMainConfig = {}
): ContentMainAPI => {
  let isDestroyed = false;

  // Internal root element
  const root = document.createElement("div");
  root.className = "content-main-container";

  // Internal state (mutable)
  let showBreakpoints = config.showBreakpoints ?? true;
  let showLanguages = config.showLanguages ?? true;
  let showThemes = config.showThemes ?? true;

  let selectedBreakpoint: BreakpointType | undefined =
    config.selectedBreakpoint;
  let selectedTheme: Theme | undefined = config.selectedTheme;
  let selectedLanguage: Language | undefined = config.selectedLanguage;
  let availableLanguages: readonly Language[] | undefined =
    config.availableLanguages;

  // Child component refs
  let breakpointsAPI: BreakpointsAPI | null = null;
  let languagesAPI: LanguagesAPI | null = null;
  let themesAPI: ThemesAPI | null = null;

  const destroyChildren = (): void => {
    breakpointsAPI?.destroy();
    languagesAPI?.destroy();
    themesAPI?.destroy();
    breakpointsAPI = null;
    languagesAPI = null;
    themesAPI = null;
  };

  const clearRoot = (): void => {
    while (root.firstChild) root.removeChild(root.firstChild);
  };

  const shouldRenderLanguages = (): boolean => {
    if (!showLanguages) return false;
    const langs =
      availableLanguages ?? (selectedLanguage ? [selectedLanguage] : []);
    return Array.isArray(langs) && langs.length > 1;
  };

  const render = (): void => {
    if (isDestroyed) return;

    // Full remount to avoid stale listeners/state
    destroyChildren();
    clearRoot();

    type Section = {
      name: "breakpoints" | "languages" | "themes";
      el: HTMLElement;
    };
    const sections: Section[] = [];

    if (showBreakpoints) {
      breakpointsAPI = createBreakpoints({
        breakpoints: ["desktop_large", "desktop", "tablet", "mobile"],
        selected: selectedBreakpoint ?? "desktop",
        onSelectionChange: (bp) => {
          selectedBreakpoint = bp;
          config.onBreakpointChange?.(bp);
        },
      });
      sections.push({ name: "breakpoints", el: breakpointsAPI.getElement() });
    }

    if (shouldRenderLanguages()) {
      const langs = (availableLanguages ?? []) as readonly Language[];
      languagesAPI = createLanguages({
        selected:
          selectedLanguage ?? (langs[0] as Language | undefined) ?? "en",
        languages: langs,
        onSelectionChange: (lang) => {
          selectedLanguage = lang;
          config.onLanguageChange?.(lang);
        },
      });
      sections.push({ name: "languages", el: languagesAPI.getElement() });
    }

    if (showThemes) {
      themesAPI = createThemes({
        selected: selectedTheme ?? "light",
        onSelectionChange: (theme) => {
          selectedTheme = theme;
          config.onThemeChange?.(theme);
        },
      });
      sections.push({ name: "themes", el: themesAPI.getElement() });
    }

    // Append sections with separators between visible neighbors
    sections.forEach((section, index) => {
      if (index > 0) {
        const sep = createSeparator({
          orientation: "vertical",
          thickness: 1,
          color: "var(--color-dark)",
          opacity: 0.1,
        });
        root.appendChild(sep);
      }
      root.appendChild(section.el);
    });
  };

  // Initial render
  render();

  const api: ContentMainAPI = {
    getElement: () => root,

    update: (partial) => {
      if (isDestroyed) return;

      if (partial.selectedBreakpoint !== undefined) {
        selectedBreakpoint = partial.selectedBreakpoint;
      }
      if (partial.selectedTheme !== undefined) {
        selectedTheme = partial.selectedTheme;
      }
      if (partial.selectedLanguage !== undefined) {
        selectedLanguage = partial.selectedLanguage;
      }
      if (partial.availableLanguages !== undefined) {
        availableLanguages = partial.availableLanguages;
      }
      if (partial.showBreakpoints !== undefined) {
        showBreakpoints = partial.showBreakpoints;
      }
      if (partial.showLanguages !== undefined) {
        showLanguages = partial.showLanguages;
      }
      if (partial.showThemes !== undefined) {
        showThemes = partial.showThemes;
      }

      render();
    },

    setVisibility: (visibility) => {
      if (isDestroyed) return;
      if (visibility.showBreakpoints !== undefined) {
        showBreakpoints = visibility.showBreakpoints;
      }
      if (visibility.showLanguages !== undefined) {
        showLanguages = visibility.showLanguages;
      }
      if (visibility.showThemes !== undefined) {
        showThemes = visibility.showThemes;
      }
      render();
    },

    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;
      destroyChildren();
      clearRoot();
      root.remove();
    },
  };

  return api;
};

export const createContentMainContainerInContainer = (
  container: HTMLElement,
  config: ContentMainConfig = {}
): ContentMainAPI => {
  const cmc = createContentMainContainer(config);
  container.appendChild(cmc.getElement());
  return cmc;
};
